
import React from 'react';
import { Split, X } from 'lucide-react';
import { ZoneNode, splitZone } from '../utils/zoneUtils';
import { cn } from '../utils/cn';

interface Props {
  zones: ZoneNode[];
  onRemove: (zoneId: string) => void;
  onExplode: (zoneId: string) => void;
  disabled?: boolean;
  /** Rendered after the last badge, e.g. the "+ Ort hinzufügen" chip. */
  trailing?: React.ReactNode;
  /**
   * flow: render badges in a plain block instead of a flex container, so they
   * wrap around floated elements (e.g. the map preview on phones).
   */
  flow?: boolean;
}

/** What a zone splits into, for the "aufteilen" affordance label. */
const CHILD_LABEL: Record<string, string> = {
  CI: 'Stadtteile/PLZ',
  RE: 'Orte/PLZ',
  DI: 'Ortsteile/PLZ',
  TO: 'Ortsteile/PLZ',
  QU: 'PLZ',
};

const TYPE_MAPPING: Record<string, string> = {
  'CO': 'Land',
  'ST': 'Bundesland',
  'RE': 'Bezirk',
  'DI': 'Stadtteil',
  'CI': 'Stadt',
  'TO': 'Gemeinde',
  'QU': 'Ortsteil',
  'ZI': 'PLZ'
};

/** Badges shown before "+ N weitere" (≈3 rows at full width); a lone hidden badge is shown instead. */
const COLLAPSED_COUNT = { mobile: 6, desktop: 12 };

export const ZoneTagCloud: React.FC<Props> = ({ zones, onRemove, onExplode, disabled, trailing, flow }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (zones.length === 0) return null;

  // Collapse by whole badges, never mid-row.
  const limit = isMobile ? COLLAPSED_COUNT.mobile : COLLAPSED_COUNT.desktop;
  const collapsible = zones.length > limit + 1;
  const visible = collapsible && !isExpanded ? zones.slice(0, limit) : zones;
  // The level ("Stadtteil", "PLZ") only tells badges apart when levels are mixed; otherwise it's a tooltip.
  const mixedTypes = new Set(zones.map((z) => z.t)).size > 1;
  const chipClass = 'inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium';

  return (
    // flow: badges are inline-flex boxes in a plain block, so they wrap around floats
    // (map preview); margins replace the flex gap. mt-2: spacing to the search field above.
    <div className={flow ? 'mt-2 [&>*]:mr-2 [&>*]:mb-2' : 'flex flex-wrap gap-2'}>
      {visible.map((zone, index) => {
        // Debug logging for malformed zones
        if (!zone.t || !zone.i) {
          console.warn('[ZoneTagCloud] Malformed zone detected at index', index, ':', JSON.stringify(zone, null, 2));
        }

        const canExplode = zone.c && zone.c.length > 0;
        const zoneName = zone.l || zone.i;
        const typeName = TYPE_MAPPING[zone.t] || zone.t || 'Unbekannt';
        const explodeLabel = `${zoneName} in ${CHILD_LABEL[splitZone(zone).parent.t] || 'Teilgebiete'} aufteilen`;

        return (
          <div
            key={zone.i}
            title={mixedTypes ? undefined : typeName}
            className={cn(
              'zone-tag inline-flex items-center gap-1.5 rounded-full border border-line bg-sunken px-3 py-1.5 text-sm text-ink',
              canExplode && 'border-dashed'
            )}
            // Kept as a desktop shortcut; the visible Split button is the primary (touch-friendly) way.
            onDoubleClick={() => !disabled && canExplode && onExplode(zone.i)}
          >
            <span className="flex items-center gap-1.5">
              {zoneName}
              {mixedTypes ? (
                <span className="rounded bg-card px-1 py-0.5 text-[0.65rem] font-semibold text-muted">{typeName}</span>
              ) : (
                <span className="sr-only">({typeName})</span>
              )}
            </span>
            {canExplode && (
              <button
                type="button"
                className="rounded-full p-0.5 text-brand hover:bg-line disabled:cursor-not-allowed disabled:opacity-30"
                onClick={(e) => {
                  e.stopPropagation();
                  !disabled && onExplode(zone.i);
                }}
                disabled={disabled}
                aria-label={explodeLabel}
                title={explodeLabel}
              >
                <Split className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="rounded-full p-0.5 text-muted opacity-70 hover:bg-line hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-30"
              onClick={(e) => {
                e.stopPropagation();
                !disabled && onRemove(zone.i);
              }}
              disabled={disabled}
              aria-label={`${zoneName} entfernen`}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        );
      })}
      {collapsible && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          className={cn(chipClass, 'bg-sunken text-muted hover:bg-line hover:text-ink')}
        >
          {isExpanded ? 'Weniger anzeigen' : `+ ${zones.length - limit} weitere`}
        </button>
      )}
      {trailing}
    </div>
  );
};
